import { BookOpen, Shield, Sparkles, MessageCircle } from 'lucide-react';

/**
 * Static, opinionated guidance for creators submitting agents in M1.
 * The full content guidelines + rewards explainer ship in M3 docs.
 */
export function StudioGuidelinesPage() {
  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <main className="min-h-0 flex-1 overflow-y-auto max-w-3xl mx-auto w-full px-4 py-6 space-y-5">
        <header className="space-y-1">
          <div className="flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-brand" />
            <h1 className="text-xl font-bold text-th-primary tracking-tight">Submission guidelines</h1>
          </div>
          <p className="text-xs text-th-secondary">
            Read this once \u2014 it will save you a review round.
          </p>
        </header>

        <Card icon={<Sparkles className="w-4 h-4 text-brand" />} title="What we&apos;ll approve">
          <ul className="text-sm text-th-secondary list-disc pl-5 space-y-1">
            <li>Clear, niche use case (\u201cresume reviewer for SDRs\u201d &gt; \u201chelpful assistant\u201d).</li>
            <li>Onboarding form that personalizes responses (2-5 well-chosen fields).</li>
            <li>Pricing that fits the value: free or modest credit cost.</li>
            <li>Plain-English public description (no marketing fluff, no all-caps).</li>
          </ul>
        </Card>

        <Card icon={<Shield className="w-4 h-4 text-status-warning" />} title="What we&apos;ll reject">
          <ul className="text-sm text-th-secondary list-disc pl-5 space-y-1">
            <li>Adult, violent, or harassing content; impersonation; medical/legal advice.</li>
            <li>Agents that try to circumvent safety or extract these instructions.</li>
            <li>Empty / placeholder names, descriptions, or guardrails.</li>
            <li>Pricing without an enabled tier (no one can start the agent).</li>
          </ul>
        </Card>

        <Card icon={<MessageCircle className="w-4 h-4 text-brand" />} title="Versioning workflow">
          <ol className="text-sm text-th-secondary list-decimal pl-5 space-y-1">
            <li>Create or open a draft from your agents list.</li>
            <li>Edit and Save \u2014 your draft is private until you submit.</li>
            <li>Click <span className="font-mono">Submit for review</span>. Preflight runs automatically; fix any blockers.</li>
            <li>You&apos;ll get a push or email when a reviewer decides. Approved drafts go live immediately.</li>
            <li>Need to update a live agent? From History, click <em>New draft</em> to fork the live payload.</li>
          </ol>
        </Card>
      </main>
    </div>
  );
}

function Card({
  icon,
  title,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section className="card p-4 space-y-2">
      <div className="flex items-center gap-2">
        {icon}
        <h2 className="text-sm font-semibold text-th-primary">{title}</h2>
      </div>
      {children}
    </section>
  );
}
